import React from 'react'
import filter from 'lodash/filter'
import map from 'lodash/map'
import { Dropdown } from 'react-bootstrap'
import { blogPermissions } from '@ztechapps/blog.common'
import { useConfirmDialog, useRole } from '@ztechapps/common.hook'
import { LinkDropdownToggle } from '@ztechapps/webui.data-tools'
import { DisabledReasonPopOver, HorizontalStack, Icon, PortalDropdownMenu, ShowIf } from '@ztechapps/webui.util'
import type { BlogCategoryNode } from '../../BlogCategory/blogCategoryTree.utils'
import { buildMoveToMenuModel, isBlogCategoryDeleteVisible } from '../../BlogCategory/blogCategoryTree.utils'
import { NO_DELETE_PERMISSION_REASON, NO_EDIT_PERMISSION_REASON } from '../../PermissionReasons'
import { useBlogCategoryTreeViewContext } from './Context'

export type BlogCategoryRowMenuProps = {
  category: BlogCategoryNode
  /** Destinations that make no sense for the category — its own subtree and parent — left out of "Move to…". */
  excludedBlogCategoryIds: string[]
  onRename: () => void
}

/** The row's vertical-dots menu: Rename, Delete (not rendered for a reserved or non-empty category) and the destinations to move to. */
export const BlogCategoryRowMenu = ({ category, excludedBlogCategoryIds, onRename }: BlogCategoryRowMenuProps) => {
  const { flat, canEdit, countAllBlogsIn, isReserved, moveBlogCategory, deleteBlogCategory } =
    useBlogCategoryTreeViewContext()
  const { hasRole: canDelete } = useRole({ roleName: blogPermissions.canDelete.value })
  const canDeleteHere = isBlogCategoryDeleteVisible({
    reserved: isReserved(category._id),
    childCount: filter(flat, { _parentId: category._id }).length,
    blogCount: countAllBlogsIn(category._id),
  })
  const { onConfirm } = useConfirmDialog({
    title: `Delete the category "${category.name}"?`,
    text: 'The category is empty, so no blog is affected.',
    confirmed: () => deleteBlogCategory(category._id),
  })

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        id={`blog-category-menu-${category._id}`}
        as={LinkDropdownToggle}
        icon="ellipsis-v"
        title="Show more actions..."
        aria-label={`Show more actions for ${category.name}`}
      />

      {/* Portalled so the tree pane, which scrolls, cannot clip the menu. */}
      <PortalDropdownMenu className="overflow-y-auto" style={{ maxHeight: '24rem' }}>
        <DisabledReasonPopOver reason={canEdit ? undefined : NO_EDIT_PERMISSION_REASON}>
          <Dropdown.Item disabled={!canEdit} onClick={onRename}>
            <HorizontalStack gap={1}>
              <Icon name="pencil" aria-hidden="true" />
              <span>Rename</span>
            </HorizontalStack>
          </Dropdown.Item>
        </DisabledReasonPopOver>
        <ShowIf condition={canDeleteHere}>
          <DisabledReasonPopOver reason={canDelete ? undefined : NO_DELETE_PERMISSION_REASON}>
            <Dropdown.Item className={canDelete ? 'text-danger' : undefined} disabled={!canDelete} onClick={onConfirm}>
              <HorizontalStack gap={1}>
                <Icon name="trash" aria-hidden="true" />
                <span>Delete</span>
              </HorizontalStack>
            </Dropdown.Item>
          </DisabledReasonPopOver>
        </ShowIf>
        <Dropdown.Divider />
        <Dropdown.Header>Move to…</Dropdown.Header>
        {map(buildMoveToMenuModel(flat, excludedBlogCategoryIds, !!category.parent), (item) => (
          <DisabledReasonPopOver key={item.key} reason={canEdit ? undefined : NO_EDIT_PERMISSION_REASON}>
            <Dropdown.Item
              disabled={item.disabled || !canEdit}
              style={
                item.depth > 1
                  ? { paddingLeft: `calc(var(--bs-dropdown-item-padding-x) + ${item.depth - 1}rem)` }
                  : undefined
              }
              onClick={() => moveBlogCategory(category._id, item.categoryId)}
            >
              {item.label}
            </Dropdown.Item>
          </DisabledReasonPopOver>
        ))}
      </PortalDropdownMenu>
    </Dropdown>
  )
}
